import type { StreamProvider } from '@vgtv/api-client';
import {
  SportUpcomingEventsResponse,
  SportUpcomingEventTransformed,
} from './types';

const SUPPORTED_ASSET_PROVIDERS = ['ab', 'vgtv'] as const;

type SupportedAssetProvider = (typeof SUPPORTED_ASSET_PROVIDERS)[number];

const transfomParticipant = (
  participantId: number,
  data: SportUpcomingEventsResponse
): SportUpcomingEventTransformed['participants'][number] => {
  const participant = data.participants[participantId];
  return {
    name: participant.name,
    image: participant.images.clubLogo?.url
      ? {
          alt: `${participant.name} logo`,
          url: `${participant.images.clubLogo.url}?rule=clip-40x40`,
        }
      : undefined,
  };
};

const getEventUrl = (
  provider: SupportedAssetProvider,
  eventId: number
): string => {
  if (provider === 'ab') {
    return `https://sportbladet-live.aftonbladet.se/_/event/_/${eventId}`;
  }

  return `https://vglive.vg.no/_/event/_/${eventId}`;
};

const transformUpcomingSportEvents =
  (provider: SupportedAssetProvider) =>
  (data: SportUpcomingEventsResponse): SportUpcomingEventTransformed[] => {
    const today = new Date().toISOString().slice(0, 10);
    const transformedEvents = data.events
      .filter(({ startDate }) => startDate.slice(0, 10) > today)
      .slice(0, 3)
      .map((event) => {
        return {
          id: event.id,
          url: getEventUrl(provider, event.id),
          participants: event.participantIds.map((participantId) =>
            transfomParticipant(participantId, data)
          ),
        };
      });

    return transformedEvents;
  };

const providerToPublisher = (provider: SupportedAssetProvider): string => {
  switch (provider) {
    case 'ab':
      return 'ab';
    case 'vgtv':
      return 'vg';
    default:
      throw new Error(`Unsupported provider: ${provider}`);
  }
};

export const fetchUpcomingSportEvents = (provider: SupportedAssetProvider) => {
  return fetch(
    `https://api.sportsnext.schibsted.io/v2/${providerToPublisher(provider)}/assets/upcoming-video-streams`
  )
    .then((response) => response.json())
    .then(transformUpcomingSportEvents(provider));
};

export const isSupportedAssetProvider = (
  provider: StreamProvider | 'brandstudio'
): provider is SupportedAssetProvider => {
  return SUPPORTED_ASSET_PROVIDERS.includes(provider as SupportedAssetProvider);
};
